/**
 * The single place the browser talks to the server.
 *
 * Every state-changing request carries the double-submit CSRF token, and a 401
 * anywhere clears the cached session so the app falls back to the sign-in
 * screen rather than rendering half-empty pages.
 */

export class ApiError extends Error {
  constructor(public status: number, message: string, public detail?: unknown) {
    super(message);
  }
}

let csrfToken = '';
export const setCsrfToken = (t: string) => { csrfToken = t; };

let onUnauthorised: (() => void) | null = null;
export const setUnauthorisedHandler = (fn: () => void) => { onUnauthorised = fn; };

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;

  const res = await fetch(url, {
    method,
    headers,
    credentials: 'same-origin',
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });

  // A 401 from the sign-in endpoint means the credentials were wrong, not that
  // a session expired. Treating the two the same told someone mistyping their
  // password that their session had ended, which is both wrong and unhelpful.
  const isSignIn = url.endsWith('/api/auth/login');

  if (res.status === 401 && !isSignIn) {
    onUnauthorised?.();
    throw new ApiError(401, 'Your session has ended. Sign in again.');
  }

  const contentType = res.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (!res.ok) throw new ApiError(res.status, `Request failed (${res.status})`);
    return (await res.text()) as unknown as T;
  }

  const payload = await res.json();
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? `Request failed (${res.status})`, payload?.detail);
  }
  return payload as T;
}

export const api = {
  get: <T,>(url: string) => request<T>('GET', url),
  post: <T,>(url: string, body?: unknown) => request<T>('POST', url, body),
  patch: <T,>(url: string, body?: unknown) => request<T>('PATCH', url, body),
  del: <T,>(url: string) => request<T>('DELETE', url),
};
